import type { ReactNode } from 'react';
import { Badge } from '@/design-system/ui/Card';
import { Icon, type IconName } from '@/design-system/ui/Icon';
import { cn } from '@/design-system/ui/cn';
import { formatDateTimeJalali } from '@/core/format/jalali';
import { toPersianDigits } from '@/core/format/numbers';
import { formatLoadPrice, loadTone, type Load } from '../model/schemas';
import { DownloadLoadButton, DriverCallButton } from './LoadActions';
import { StatusTimeline } from './StatusTimeline';

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 py-2 text-sm">
      <dt className="shrink-0 text-ink-2">{label}</dt>
      <dd className="text-end font-medium">{value || '—'}</dd>
    </div>
  );
}

function Section({ title, children, aside }: { title: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="mt-6">
      <div className="mb-3 flex items-center gap-2">
        <span className="h-5 w-1 rounded-full bg-gradient-to-b from-turquoise to-success" aria-hidden />
        <h3 className="font-bold">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** One fact of the shipment as a small card: round icon, quiet label, strong value. */
function InfoTile({ icon, label, children, className }: { icon: IconName; label: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-center gap-3 rounded-[14px] border border-line-subtle bg-fill/60 p-3', className)}>
      <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-paper text-action shadow-card ring-1 ring-line-subtle">
        <Icon name={icon} size={19} />
      </span>
      <div className="min-w-0 flex-1">
        <dt className="text-xs text-ink-2">{label}</dt>
        <dd className="mt-0.5 break-words text-sm font-bold text-ink">{children}</dd>
      </div>
    </div>
  );
}

const mobileText = (m: string) => (m ? toPersianDigits(m) : '');

function PersonBlock({ title, p }: { title: string; p: Load['sender'] }) {
  return (
    <div className="rounded-md border border-line-subtle bg-fill/60 p-3">
      <h4 className="mb-1 text-sm font-bold text-navy">{title}</h4>
      <dl className="divide-y divide-line-subtle">
        <Row label="نام" value={p.name} />
        <Row label="موبایل" value={mobileText(p.mobile)} />
        {p.nationalCode && <Row label="کد ملی" value={toPersianDigits(p.nationalCode)} />}
      </dl>
    </div>
  );
}

export function LoadDetails({ load, showDownload = true }: { load: Load; showDownload?: boolean }) {
  const totalCount = load.packages.reduce((s, p) => s + p.count, 0);
  const totalWeight = load.packages.reduce((s, p) => s + p.weight, 0);
  const [amount = '', unit = ''] = toPersianDigits(formatLoadPrice(load.price)).replace(/,/g, '٬').split(' ');
  const price = { amount, unit };
  const [day, time] = load.createdAt ? formatDateTimeJalali(load.createdAt).split(/\s+/) : [];
  const when = day && time ? { date: toPersianDigits(day.replace(/-/g, '/')), time: toPersianDigits(time) } : null;

  return (
    <>
      {(load.fragile || load.insurance || load.urgent || showDownload) && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {load.urgent && <Badge tone="danger">فوری</Badge>}
          {load.fragile && <Badge tone="warning">شکستنی</Badge>}
          {load.insurance && <Badge tone="success">بیمه‌شده</Badge>}
          {showDownload && <span className="ms-auto"><DownloadLoadButton loadId={load.id} /></span>}
        </div>
      )}

      {load.driver && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-primary-light/50 p-3">
          <div className="flex items-center gap-3">
            <span className="grid size-10 shrink-0 place-items-center rounded-md bg-solid text-white"><Icon name="user" size={20} /></span>
            <div>
              <p className="text-xs text-ink-2">اطلاعات راننده</p>
              <p className="text-sm font-bold">{load.driver.name || '—'}</p>
              {load.driver.mobile && <p className="text-xs text-ink-2">{mobileText(load.driver.mobile)}</p>}
            </div>
          </div>
          <DriverCallButton mobile={load.driver.mobile} />
        </div>
      )}

      <dl className="grid gap-2.5 sm:grid-cols-2">
        <InfoTile icon="verified" label="وضعیت"><Badge tone={loadTone(load)}>{load.statusLabel}</Badge></InfoTile>
        <InfoTile icon="pin" label="مقصد">{load.destination || '—'}</InfoTile>
        <InfoTile icon="route" label="نوع تحویل">{load.delivery || '—'}</InfoTile>
        <InfoTile icon="wallet" label="نحوه پرداخت">{load.payment || '—'}</InfoTile>
        <InfoTile icon="calendar" label="تاریخ ثبت" className={load.address ? undefined : 'sm:col-span-2'}>
          {when ? (
            <span className="flex flex-wrap items-center gap-x-2">
              <span>{when.date}</span>
              <span className="font-medium text-ink-2">ساعت {when.time}</span>
            </span>
          ) : '—'}
        </InfoTile>
        {load.address && <InfoTile icon="pin" label="آدرس تحویل">{load.address}</InfoTile>}

        <div className="flex items-center justify-between gap-3 rounded-[14px] bg-secondary-light p-4 ring-1 ring-turquoise/20 sm:col-span-2">
          <div>
            <dt className="text-xs text-ink-2">هزینه ارسال</dt>
            <dd className="mt-0.5 text-xl font-extrabold text-action-dark">
              {price.amount}
              <span className="ms-1.5 text-sm font-bold text-ink-2">{price.unit}</span>
            </dd>
          </div>
          <span className="grid size-11 shrink-0 place-items-center rounded-full bg-paper text-action shadow-card"><Icon name="wallet" size={22} /></span>
        </div>
      </dl>
      {load.description && (
        <p className="mt-3 flex items-start gap-2 whitespace-pre-wrap rounded-md bg-fill p-3 text-sm text-ink-2">
          <Icon name="info" size={16} className="mt-0.5 shrink-0 text-navy" />
          {load.description}
        </p>
      )}

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <PersonBlock title="فرستنده" p={load.sender} />
        <PersonBlock title="گیرنده" p={load.receiver} />
      </div>

      {load.packages.length > 0 && (
        <Section title="بسته‌ها" aside={<Badge tone="info">{toPersianDigits(load.packages.length)} بسته</Badge>}>
          <ul className="flex flex-col gap-2">
            {load.packages.map((p, i) => (
              <li key={i} className="flex items-center gap-3 rounded-md border border-line-subtle bg-fill/60 p-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-md bg-primary-light text-navy"><Icon name="box" size={18} /></span>
                <span className="flex-1 text-sm font-bold">{p.title}</span>
                <span className="text-center text-xs text-ink-2">تعداد<b className="block text-sm text-ink">{toPersianDigits(p.count)}</b></span>
                <span className="text-center text-xs text-ink-2">وزن (kg)<b className="block text-sm text-ink">{toPersianDigits(p.weight)}</b></span>
              </li>
            ))}
          </ul>
          <p className="mt-2 flex gap-5 px-1 text-xs text-ink-2">
            <span>مجموع تعداد: <b className="text-ink">{toPersianDigits(totalCount)}</b></span>
            <span>مجموع وزن: <b className="text-ink">{toPersianDigits(Math.round(totalWeight * 10) / 10)} kg</b></span>
          </p>
        </Section>
      )}

      <Section title="وضعیت مرسوله">
        <StatusTimeline history={load.history} />
      </Section>
    </>
  );
}
